import type { Component } from '../router';
import { icon } from '../components/icons';
import { ViewportGate } from '../components/ViewportGate';
import { confirmDialog } from '../components/ConfirmDialog';
import { withPasskeyStepUp } from '../components/PasskeyModal';
import { ApiError, api } from '../services/api';
import { deletePasskey, enrollPasskey, isPasskeySupported, listPasskeys, type PasskeySummary } from '../services/webauthn';
import { formatLastUsed } from './SessionsPage';

/**
 * Passkeys — account-security screen. Lists the user's enrolled
 * passkeys and lets them add a backup or remove one. Self-service half of the
 * passkey lifecycle; admin-mediated recovery is .
 *
 * Dual UX (viewport-adaptive, not stretched): passkeys are desktop-only
 *, so phone/tablet render an information panel pointing the user
 * to a desktop, while desktop renders the full manage list. That's a different
 * component per class, not a reflowed one.
 */

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

function addErrorCopy(err: unknown): string {
  if (err instanceof DOMException) {
    // Safari's cross-device (QR) flow reports an already-enrolled authenticator
    // as NotAllowedError rather than InvalidStateError, so the message has to
    // cover both that and a genuine cancel/timeout.
    if (err.name === 'NotAllowedError') {
      return 'Couldn’t add the passkey — it may have been cancelled or timed out, or that authenticator already has a passkey for your account.';
    }
    if (err.name === 'InvalidStateError') return 'That authenticator already has a passkey on your account.';
    return 'Your authenticator couldn’t complete setup. Try again.';
  }
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    if (err.status === 403) return 'Passkeys are available to office roles on a desktop.';
    return body?.error ?? 'Could not set up the passkey. Try again.';
  }
  return 'Could not reach the server. Check your connection.';
}

export class PasskeyManageView implements Component {
  private root: HTMLElement | null = null;
  private listHost: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private passkeys: PasskeySummary[] = [];
  private controller: AbortController | null = null;
  private destroyed = false;

  mount(host: HTMLElement): void {
    const section = node('section', 'passkeys');

    const header = node('header', 'passkeys__header');
    header.append(
      node('h1', 'passkeys__title', 'Passkeys'),
      node(
        'p',
        'passkeys__sub',
        'Passkeys protect sensitive actions like approvals and reports. Add a backup so you’re never locked out, and remove any you no longer use.',
      ),
    );

    const status = node('p', 'passkeys__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.dataset.visible = 'false';
    this.statusEl = status;

    const list = node('div', 'passkeys__list');
    this.listHost = list;

    section.append(header, status, list);
    host.append(section);
    this.root = section;

    if (!isPasskeySupported()) {
      this.renderUnsupported();
      return;
    }
    void this.load();
  }

  unmount(): void {
    this.destroyed = true;
    this.controller?.abort();
    this.controller = null;
    this.root?.remove();
    this.root = null;
  }

  private async load(): Promise<void> {
    if (this.listHost) this.listHost.replaceChildren(node('p', 'passkeys__placeholder', 'Loading your passkeys…'));
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const passkeys = await listPasskeys(this.controller.signal);
      if (this.destroyed) return;
      this.passkeys = passkeys;
      this.renderList();
    } catch (err) {
      if (this.destroyed || (err instanceof DOMException && err.name === 'AbortError')) return;
      this.renderError();
    }
  }

  private renderUnsupported(): void {
    this.listHost?.replaceChildren(
      node('p', 'passkeys__placeholder', 'This browser doesn’t support passkeys. Use a recent Chrome, Safari, or Firefox.'),
    );
  }

  private renderError(): void {
    if (!this.listHost) return;
    const box = node('div', 'passkeys__error');
    box.append(node('p', 'passkeys__placeholder', 'Could not load your passkeys. Check your connection.'));
    const retry = node('button', 'passkeys__retry', 'Try again');
    retry.type = 'button';
    retry.addEventListener('click', () => void this.load());
    box.append(retry);
    this.listHost.replaceChildren(box);
  }

  private renderList(): void {
    if (!this.listHost) return;
    const frag = document.createDocumentFragment();

    if (this.passkeys.length === 0) {
      frag.append(node('p', 'passkeys__placeholder', 'You haven’t set up a passkey yet.'));
    } else {
      const now = new Date();
      const ul = node('ul', 'passkeys__cards');
      for (const k of this.passkeys) {
        const li = node('li', 'passkey-card');
        const head = node('div', 'passkey-card__head');
        const mark = icon('chevron-right');
        mark.classList.add('passkey-card__mark');
        head.append(mark, node('span', 'passkey-card__label', k.label?.trim() || 'Passkey'));
        const meta = node('dl', 'passkey-card__meta');
        meta.append(
          metaPair('Added', k.createdAt ? formatLastUsed(now, k.createdAt) : 'Unknown'),
          metaPair('Last used', k.lastUsedAt && !k.lastUsedAt.startsWith('0001') ? formatLastUsed(now, k.lastUsedAt) : 'Never'),
        );
        li.append(head, meta, this.removeButton(k));
        ul.append(li);
      }
      frag.append(ul);
    }

    const actions = node('div', 'passkeys__actions');
    const add = node('button', 'passkeys__add', this.passkeys.length === 0 ? 'Set up a passkey' : 'Add another passkey');
    add.type = 'button';
    add.addEventListener('click', () => void this.onAdd(add));
    actions.append(add);

    // With a passkey enrolled, let the user prove step-up works end-to-end: a
    // sensitive-op call → 401 → the passkey assertion modal → retry → 200. This
    // is the only UI that exercises the assertion ceremony until a real
    // sensitive feature (certs/exports/reports) wires the same withPasskeyStepUp.
    if (this.passkeys.length > 0) {
      const verify = node('button', 'passkeys__verify', 'Test step-up');
      verify.type = 'button';
      verify.addEventListener('click', () => void this.onVerify(verify));
      actions.append(verify);
    }
    frag.append(actions);

    this.listHost.replaceChildren(frag);
  }

  private async onVerify(btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    btn.dataset.pending = 'true';
    try {
      // Hits the passkey-gated demonstrator; withPasskeyStepUp drives the
      // assertion modal on the 401 and retries once it succeeds.
      await withPasskeyStepUp(() => api('/api/sensitive/demo'));
      if (this.destroyed) return;
      this.setStatus('Passkey verified — step-up works.', 'success');
    } catch (err) {
      if (this.destroyed) return;
      this.setStatus(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Passkey check cancelled or timed out.'
          : 'Step-up verification failed. Try again.',
        'error',
      );
    } finally {
      if (!this.destroyed) {
        btn.disabled = false;
        delete btn.dataset.pending;
      }
    }
  }

  private removeButton(k: PasskeySummary): HTMLButtonElement {
    const btn = node('button', 'passkey-card__remove', 'Remove');
    btn.type = 'button';
    btn.setAttribute('aria-label', `Remove ${k.label?.trim() || 'passkey'}`);
    btn.addEventListener('click', () => void this.onRemove(k, btn));
    return btn;
  }

  private async onAdd(btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    btn.dataset.pending = 'true';
    try {
      await enrollPasskey();
      if (this.destroyed) return;
      this.setStatus('Passkey added.', 'success');
      await this.load();
    } catch (err) {
      if (this.destroyed) return;
      btn.disabled = false;
      delete btn.dataset.pending;
      this.setStatus(addErrorCopy(err), 'error');
    }
  }

  private async onRemove(k: PasskeySummary, btn: HTMLButtonElement): Promise<void> {
    const last = this.passkeys.length === 1;
    const ok = await confirmDialog({
      title: 'Remove this passkey?',
      message: last
        ? 'This is your only passkey. After removing it you’ll be asked to set one up again next time you sign in on a desktop.'
        : 'This passkey will no longer work for verifying sensitive actions. Your other passkeys keep working.',
      confirmLabel: 'Remove',
      cancelLabel: 'Keep',
      tone: 'danger',
    });
    if (!ok || this.destroyed) return;

    btn.disabled = true;
    btn.dataset.pending = 'true';
    try {
      await deletePasskey(k.credentialId);
      if (this.destroyed) return;
      this.passkeys = this.passkeys.filter((x) => x.credentialId !== k.credentialId);
      this.renderList();
      this.setStatus('Passkey removed.', 'success');
    } catch (err) {
      if (this.destroyed) return;
      btn.disabled = false;
      delete btn.dataset.pending;
      this.setStatus(err instanceof ApiError && err.status === 404 ? 'That passkey was already removed.' : 'Could not remove the passkey. Try again.', 'error');
    }
  }

  private setStatus(message: string, tone: 'success' | 'error'): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.dataset.tone = tone;
    this.statusEl.dataset.visible = 'true';
  }
}

export class PasskeyInfoView implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const section = node('section', 'passkeys');
    section.dataset.variant = 'info';

    const back = node('a', 'passkeys__back');
    back.href = '/me';
    const chevron = icon('chevron-right');
    chevron.classList.add('passkeys__back-chevron');
    back.append(chevron, document.createTextNode('Me'));

    section.append(
      back,
      node('h1', 'passkeys__title', 'Passkeys'),
      node(
        'p',
        'passkeys__sub',
        'Passkeys are set up and managed on a desktop computer — sensitive actions that need them are desktop-only. Open the portal on your desktop to add or remove a passkey.',
      ),
    );
    host.append(section);
    this.root = section;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}

export function PasskeysPage(): Component {
  return new ViewportGate({
    phone: () => new PasskeyInfoView(),
    tablet: () => new PasskeyInfoView(),
    desktop: () => new PasskeyManageView(),
  });
}

function metaPair(term: string, value: string): HTMLElement {
  const wrap = node('div', 'passkey-card__meta-row');
  wrap.append(node('dt', undefined, term), node('dd', undefined, value));
  return wrap;
}
